import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Typography,
  Box,
  Paper,
  InputAdornment,
  Divider,
} from "@mui/material";
import {
  Search as SearchIcon,
  Business as BusinessIcon,
  Receipt as OrderIcon,
  Person as ClientIcon,
} from "@mui/icons-material";
import { GetOrdersByAccountNumber } from "../../services/BottleOrdersService";

const BottleOrderSelectorDialog = ({
  open,
  onClose,
  onSelect,
  accountNumber,
}) => {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (open && accountNumber) {
      loadOrders(refreshCount, accountNumber);
    }
  }, [open, refreshCount, accountNumber]);

  const loadOrders = async (count, accNo) => {
    try {
      setLoading(true);
      const response = await GetOrdersByAccountNumber(count, accNo);
      const data = response.results;
      if (data?.length < 200) setHasMore(false);
      if (count === 0) {
        setOrders(data || []);
      } else {
        setOrders((prev) => {
          const newOnes = (data || []).filter(
            (o) => !prev.some((p) => p.orderNumber === o.orderNumber)
          );
          return [...prev, ...newOnes];
        });
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => setSearch(e.target.value);

  const filteredOrders = orders.filter((o) => {
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    return (
      o.orderNumber?.toString().includes(term) ||
      o.clientName?.toLowerCase().includes(term)
    );
  });

  const handleSelect = (order) => {
    setSelectedOrder(order);
    onSelect(order);
    onClose();
  };

  const handleLoadMore = () => {
    setRefreshCount((prev) => prev + 1);
  };

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setOrders([]);
      setSearch("");
      setRefreshCount(0);
      setHasMore(true);
      setSelectedOrder(null);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      TransitionProps={{
        onEntered: () => {
          searchInputRef.current?.focus();
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: "60vh",
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          background: "linear-gradient(135deg, #1871CA 0%, #1871CA 100%)",
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "move",
          userSelect: "none",
        }}
      >
        <BusinessIcon />
        Select Order
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3 }}>
        <Box sx={{ mb: 3, mt: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontWeight: 500 }}
            >
              Total Orders:{" "}
              <Box component="span" sx={{ color: "#1871CA", fontWeight: 600 }}>
                {orders.length}
              </Box>
            </Typography>
            {search.trim() && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontWeight: 500 }}
              >
                Matched:{" "}
                <Box
                  component="span"
                  sx={{ color: "#1871CA", fontWeight: 600 }}
                >
                  {filteredOrders.length}
                </Box>
              </Typography>
            )}
          </Box>
          <TextField
            value={search}
            inputRef={searchInputRef}
            onChange={handleSearchChange}
            placeholder="Search by Order Number or Client Name..."
            fullWidth
            variant="outlined"
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "grey.50",
                height: "44px",
                transition: "all 0.2s",
                "&:hover": {
                  bgcolor: "grey.100",
                },
                "&.Mui-focused": {
                  bgcolor: "white",
                  boxShadow: "0 0 0 2px rgba(24, 113, 202, 0.1)",
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {loading && orders.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <CircularProgress size={40} />
          </Box>
        ) : filteredOrders.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
            <SearchIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6">No Orders Found</Typography>
            <Typography variant="body2">
              Try a different search term.
            </Typography>
          </Box>
        ) : (
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              maxHeight: 400,
              overflowY: "auto",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid rgba(0,0,0,0.08)",
            }}
          >
            <List dense sx={{ p: 0 }}>
              {filteredOrders.map((o, idx) => (
                <React.Fragment key={o.orderNumber}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleSelect(o)}
                      sx={{
                        py: 1,
                        px: 2,
                        minHeight: "48px",
                        bgcolor:
                          selectedOrder?.orderNumber === o.orderNumber
                            ? "rgba(24, 113, 202, 0.08)"
                            : "inherit",
                        "&:hover": {
                          bgcolor: "rgba(24, 113, 202, 0.04)",
                        },
                        transition: "all 0.2s",
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <OrderIcon
                                sx={{ fontSize: 16, color: "#1871CA" }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: "#1871CA",
                                }}
                              >
                                #{o.orderNumber}
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{ color: "text.disabled" }}
                            >
                              -
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <ClientIcon
                                sx={{ fontSize: 14, color: "text.secondary" }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "text.secondary",
                                  fontSize: "0.875rem",
                                }}
                              >
                                {o.clientName}
                              </Typography>
                            </Box>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  {idx < filteredOrders.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        )}

        {!loading && hasMore && (
          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleLoadMore}
              disabled={loading}
              sx={{
                borderRadius: 2,
                px: 4,
                borderColor: "#1871CA",
                color: "#1871CA",
                "&:hover": {
                  borderColor: "#1871CA",
                  bgcolor: "rgba(24, 113, 202, 0.04)",
                },
              }}
            >
              {loading ? "Loading..." : "Load More"}
            </Button>
          </Box>
        )}

        {loading && orders.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: "grey.50" }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BottleOrderSelectorDialog;
